import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import type React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import styles from "./Avatar.module.scss";

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  alt?: string;
  name?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Circle for people (the default); square for things such as products or integrations. */
  shape?: "circle" | "square";
  invert?: boolean;
  mono?: boolean;
  src?: string;
  initialCount?: number;
  ref?: React.Ref<HTMLDivElement>;
}

const getInitials = (value?: string, initialCount = 2) => {
  if (!value) return "";
  return value
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, initialCount)
    .toUpperCase();
};

const DefaultAvatar = withRefProp((props: AvatarProps) => {
  const {
    className,
    alt,
    name,
    size = "lg",
    shape = "circle",
    invert = false,
    mono = false,
    src,
    initialCount = 2,
    children,
    ref,
    ...otherProps
  } = props;

  const fallback = children ?? getInitials(name ?? alt, initialCount);

  return (
    <div
      ref={ref}
      className={clsx(styles.avatar, className)}
      data-size={size}
      data-shape={shape}
      data-invert={invert ? "true" : undefined}
      data-mono={mono ? "true" : undefined}
      data-deci-avatar="true"
      title={alt ?? name}
      {...otherProps}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className={styles.image}
        />
      ) : (
        fallback
      )}
    </div>
  );
});

const Avatar = createPluggable<AvatarProps>("deci.avatar", DefaultAvatar);

export default Avatar;
